import { Dispatch, ReactNode, SetStateAction, createContext, useContext, useState } from 'react'

import { PRODUCT_CATEGORIES } from 'enums/categories'

export type AdvertCategory = 'arac-kiralama' | 'otel' | 'ucak-bileti' | 'transfer' | 'otobus-bileti'

export interface AdvertItem {
  image: string
  link: string
  alt: string
  width: number
  height: number
  category: (typeof PRODUCT_CATEGORIES)[keyof typeof PRODUCT_CATEGORIES]
}

interface AdvertContextstate {
  category: keyof typeof PRODUCT_CATEGORIES
  selectedCategoryIndex?: number
}

interface AdvertContextActions {
  setCategory: Dispatch<SetStateAction<keyof typeof PRODUCT_CATEGORIES>>
}

export const AdvertContext = createContext<Partial<AdvertContextstate & AdvertContextActions>>({})

export const useAdvertState = (adverts?: AdvertItem[]) => {
  const context = useContext(AdvertContext)

  if (!context) {
    throw new Error('useAdvertState must be used within a AdvertStateProvider')
  }
  const selectedCategoryIndex = adverts?.length ? adverts.findIndex((advert) => advert.category === PRODUCT_CATEGORIES[context?.category || 'ucak-bileti']) : 0
  return {
    ...context,
    selectedCategoryIndex,
  }
}

type AdvertStateContextProps = {
  children: ReactNode
  value: AdvertContextstate
  adverts?: AdvertItem[]
}

export const AdvertStateProvider = (props: AdvertStateContextProps) => {
  const { children, value, adverts } = props

  const [category, setCategory] = useState<keyof typeof PRODUCT_CATEGORIES>(value?.category || 'ucak-bileti')
  const [selectedCategoryIndex, setSelectedCategoryIndex] = useState<number>(value?.selectedCategoryIndex || 0)

  function changeCategory(c) {
    setCategory(c)
    setSelectedCategoryIndex(adverts?.length ? adverts.findIndex((advert) => advert.category === PRODUCT_CATEGORIES[category]) : 0)
  }

  const $contextValue: AdvertContextstate & AdvertContextActions = {
    category,
    selectedCategoryIndex,
    setCategory: changeCategory,
  }

  return <AdvertContext.Provider value={$contextValue}>{children}</AdvertContext.Provider>
}
